// In-game HUD help strings (Spanish) — the settings modal, shortcuts,
// help modal and footer sections, split out of gameEs.ts (mirror of
// ./gameHelpEn.ts). Every GameHelpKey must be present (checked by type).

import type { GameHelpKey } from './gameHelpEn.js';

export const gameHelpEs: Record<GameHelpKey, string> = {
  // Settings modal
  'settings.tab.audio': 'Sonido',
  'settings.tab.controls': 'Controles',
  'settings.tab.graphics': 'Gráficos',
  'settings.tab.gameplay': 'Juego',
  'settings.mute': 'Silenciar',
  'settings.unmute': 'Activar sonido',
  'settings.audio.master': 'General',
  'settings.audio.music': 'Música',
  'settings.audio.sfx': 'Efectos',
  'settings.controls.title': 'Teclado y ratón',
  'settings.controls.scrollSpeed': 'Velocidad de desplazamiento',
  'settings.controls.rebindSoon': 'La reasignación de teclas llegará en una próxima actualización.',
  'settings.graphics.title': 'Pantalla',
  'settings.graphics.clouds': 'Nubes',
  'settings.graphics.cloudsHint': 'Nubes a la deriva y sus sombras suaves sobre el mapa',
  'settings.graphics.minimapBox': 'Recuadro de cámara del minimapa',
  'settings.graphics.minimapBoxHint': 'Muestra el área que ve la cámara 3D en el minimapa',
  'settings.graphics.particles': 'Efectos de partículas',
  'settings.graphics.particlesHint': 'Partículas ambientales en el mapa hexagonal',
  'settings.graphics.fauna': 'Vida ambiental',
  'settings.graphics.faunaHint': 'Peces, patos y otra fauna en el mapa',
  'settings.graphics.grid': 'Mostrar cuadrícula hexagonal',
  'settings.graphics.gridHint': 'Muestra los bordes de los hexágonos en el mapa',
  'settings.graphics.doubleBoard': 'Tablero grande',
  'settings.graphics.doubleBoardHint': 'Renderiza el mapa al doble de tamaño con más detalle',
  'settings.graphics.unitQuality': 'Detalle de unidades',
  'settings.graphics.unitQualityHint':
    'Vóxel: bloques, lo más ligero. Suave: las mismas unidades, suavizadas. Modelos 3D: mallas creadas a mano donde existan (suave en el resto); más GPU.',
  'settings.graphics.unitQuality.voxel': 'Vóxel',
  'settings.graphics.unitQuality.smooth': 'Suave',
  'settings.graphics.unitQuality.mesh': 'Modelos 3D',
  'settings.graphics.hifiUnits': 'Unidades de alta fidelidad',
  'settings.graphics.hifiUnitsHint': 'Modelos redondeados y suavizados (más GPU)',
  'settings.graphics.dayNight': 'Ciclo día/noche',
  'settings.graphics.dayNightHint': 'Anima la luz diurna y enciende antorchas nocturnas',
  'settings.graphics.territory': 'Mostrar territorio',
  'settings.graphics.territoryHint': 'Colorea y delimita las casillas según su controlador',
  'settings.gameplay.title': 'Juego',
  'settings.gameplay.autosave': 'Autoguardado',
  'settings.gameplay.autosaveHint': 'Guarda automáticamente en cada tick del imperio',
  'settings.gameplay.tooltips': 'Información de casillas',
  'settings.gameplay.tooltipsHint': 'Muestra información al pasar el cursor por las casillas',
  'settings.gameplay.pauseAway': 'Pausar al salir',
  'settings.gameplay.pauseAwayHint':
    'Pausa la partida individual cuando cambias de pestaña o de ventana',
  'settings.gameplay.pauseDanger': 'Pausar ante peligro',
  'settings.gameplay.pauseDangerHint': 'Pausa y muestra lo ocurrido cuando atacan a tus ciudades',
  'settings.gameplay.pauseDanger.major': 'Ciudad perdida',
  'settings.gameplay.pauseDanger.all': 'Cualquier ataque',
  'settings.gameplay.combatOverlay': 'Vista de combate',
  'settings.gameplay.combatOverlayHint':
    'Resumen, ventana y sacudida en el mapa durante los enfrentamientos',
  'settings.gameplay.combatOverlay.drawer': 'Resumen',
  'settings.gameplay.combatOverlay.modal': 'Ventana+Resumen',
  'settings.gameplay.matchSettings': 'Ajustes de la partida',

  'match.victory': 'Condiciones de victoria',
  'match.tradeRoutes': 'Rutas comerciales',
  'match.tradeRoutesHint': 'Activa la victoria por rutas comerciales',
  'match.formations': 'Formaciones',
  'match.formationsHint': 'Activa el combate con formaciones',
  'match.minEmpireTick': 'Tick mínimo del imperio',
  'match.minEmpireTickHint': 'Tick mínimo del imperio antes de una victoria sin conquista',
  'match.sustainTicks': 'Ticks sostenidos',
  'match.sustainTicksHint': 'Ticks consecutivos del imperio para consolidar la victoria',
  'match.ai': 'Personalidad de la IA',
  'match.shuffleInterval': 'Intervalo de cambio',
  'match.shuffleIntervalHint': 'Vuelve a sortear la personalidad de la IA cada N ticks (0 = nunca)',
  'match.aggressionMin': 'Agresividad mínima',
  'match.gameSpeed': 'Velocidad de juego',
  'match.incomeInterval': 'Intervalo de ingresos',
  'match.incomeIntervalHint': 'Ticks entre cobros de ingresos',
  'match.empireInterval': 'Intervalo del imperio',
  'match.empireIntervalHint': 'Ticks entre decisiones del imperio',
  'match.movementReset': 'Intervalo de movimiento',
  'match.movementResetHint': 'Ticks entre recargas de movimiento',
  'match.slowIncome': 'Intervalo de ingresos lentos',
  'match.slowIncomeHint': 'Ticks entre cobros de ingresos lentos',
  'match.aiMove': 'Intervalo de la IA',
  'match.aiMoveHint': 'Ticks entre decisiones de movimiento de la IA',
  'match.siege': 'Asedio',
  'match.evalInterval': 'Intervalo de evaluación',
  'match.evalIntervalHint': 'Ticks entre evaluaciones del asedio',
  'match.moodPenalty': 'Penalización de ánimo',
  'match.siegeMoodPenaltyHint': 'Penalización de ánimo por tick de asedio',
  'match.surrenderMood': 'Umbral de ánimo para rendirse',
  'match.surrenderMoodHint': 'Umbral de ánimo para ofrecer la rendición',
  'match.surrenderLoyalty': 'Umbral de lealtad para rendirse',
  'match.surrenderLoyaltyHint': 'Umbral de lealtad para ofrecer la rendición',
  'match.minSiegeTicks': 'Ticks mínimos de asedio',
  'match.minSiegeTicksHint': 'Ticks mínimos antes de ofrecer la rendición',
  'match.capitalMood': 'Umbral de ánimo de la capital',
  'match.capitalMoodHint': 'Umbral de ánimo para la rendición de una capital asediada',
  'match.capitalMinTicks': 'Ticks mínimos de asedio a la capital',
  'match.capitalMinTicksHint': 'Ticks mínimos antes de que la capital ofrezca la rendición',
  'match.espionage': 'Espionaje',
  'match.intelDepth1': 'Profundidad de informes 1',
  'match.intelDepth1Hint': 'Primera profundidad de entrega',
  'match.intelDepth2': 'Profundidad de informes 2',
  'match.intelDepth2Hint': 'Segunda profundidad de entrega',
  'match.detectAdjacent': 'Detección adyacente',
  'match.detectAdjacentHint': 'Probabilidad de detección en casillas adyacentes (%)',
  'match.detect2Hex': 'Detección a 2 hexágonos',
  'match.detect2HexHint': 'Probabilidad de detección a 2 hexágonos de distancia (%)',
  'match.bodyguard': 'Bloqueo de escolta',
  'match.bodyguardHint': 'Probabilidad de bloqueo de la escolta (%)',
  'match.sacrifice': 'Sacrificio',
  'match.minPopulation': 'Población mínima',
  'match.minPopulationHint': 'Población mínima necesaria para sacrificar',
  'match.faithReturn': 'Retorno de fe',
  'match.faithReturnHint': 'Numerador/denominador del retorno de fe',
  'match.sacrificeMoodPenaltyHint': 'Penalización de ánimo por sacrificio',
  'match.loyaltyPenalty': 'Penalización de lealtad',
  'match.loyaltyPenaltyHint': 'Penalización de lealtad por sacrificio',
  'match.combat': 'Combate',
  'match.surrenderHp': 'Umbral de PV para rendirse',
  'match.surrenderHpHint': 'Porcentaje de PV por debajo del cual se rinde',
  'match.conversionCost': 'Coste de conversión',
  'match.conversionCostHint': 'Porcentaje del coste de conversión',
  'match.economy': 'Economía',
  'match.baseFood': 'Comida base por ciudad',
  'match.baseFoodHint': 'Producción base de comida por ciudad',
  'match.lootGold': 'Botín de oro por habitante',
  'match.lootGoldHint': 'Oro por habitante al saquear una ciudad',
  'match.lootIndustry': 'Botín de industria por nivel',
  'match.lootIndustryHint': 'Industria por nivel al saquear una ciudad',

  // Shortcuts
  'keys.esc': 'Anular la selección y cerrar paneles',
  'keys.debug': 'Mostrar u ocultar el HUD de depuración',
  'keys.chat': 'Abrir o cerrar el chat (multijugador)',

  // Help modal
  'help.title': 'Cómo jugar',
  'help.label': 'Cómo jugar',
  'help.close': 'Cerrar la ayuda',
  'help.controls': 'Controles',
  'help.key.clickTile': 'Clic en casilla',
  'help.key.clickTileDesc': 'Selecciona la casilla y muestra su información',
  'help.key.clickCity': 'Clic en ciudad',
  'help.key.clickCityDesc': 'Abre el panel de construcción y guarnición',
  'help.key.clickUnit': 'Clic en unidad',
  'help.key.clickUnitDesc': 'Selecciona la unidad y muestra sus habilidades',
  'help.key.rightClick': 'Clic derecho en casilla',
  'help.key.rightClickDesc': 'Mueve la unidad seleccionada',
  'help.resources': 'Recursos',
  'help.res.food': 'Comida',
  'help.res.foodDesc': 'Granjas, pastos y pesca. Impulsa el crecimiento de la población.',
  'help.res.industry': 'Industria',
  'help.res.industryDesc': 'Minas, canteras y talleres. Construye edificios y unidades.',
  'help.res.knowledge': 'Conocimiento',
  'help.res.knowledgeDesc':
    'Bibliotecas y academias. Gasta 150 para una mejora de investigación de 8 turnos (+50 % de industria y +30 % de conocimiento).',
  'help.res.faith': 'Fe',
  'help.res.faithDesc':
    'Templos y santuarios. Potencia las unidades religiosas. Mucha fe genera tensión de crisis: cálmala con Peregrinación (100 de oro) o Reforma (50 de fe).',
  'help.res.gold': 'Oro',
  'help.res.goldDesc':
    'Rutas comerciales, mercados e impuestos. Sirve para edificios, peregrinaciones y diplomacia.',
  'help.combat': 'Consejos rápidos de combate',
  'help.combat.tip1':
    'Las picas vencen a la caballería (1,5x). La caballería vence a las unidades a distancia (1,4x). Las unidades a distancia vencen a las picas (1,4x).',
  'help.combat.tip2':
    'La magia vence al asedio (1,4x). El asedio vence a las unidades a distancia (1,1x) y a los edificios.',
  'help.combat.tip3': 'Defiende en {hills}, {mountains} o {cities}.',
  'help.combat.hills': 'colinas (+2)',
  'help.combat.mountains': 'montañas (+3)',
  'help.combat.cities': 'ciudades (+2)',
  'help.combat.tip4':
    'Cambia la formación de cada unidad: Línea vence a Columna, Cuña vence a Línea y Posición vence a Escaramuza.',
  'help.victory': 'Formas de ganar',
  'help.victory.conquest': 'Controla todas las ciudades',
  'help.victory.economy': 'Oro y rutas comerciales por encima del umbral',
  'help.victory.science': 'Acumula conocimiento por encima del umbral',
  'help.victory.faith': 'Fe y templos por encima del umbral',
  'help.victory.diplomacy': 'Honor y alianzas con todos los jugadores',
  'help.fullGuide': 'Guía completa:',
  'help.gotIt': 'Entendido',

  // Footer hint
  'footer.togglesDebug': 'muestra la depuración',
  'footer.devtools': 'DevTools en',
};
